import { rpc } from '../../lib/rpc';
import { sgToday, sgWeekStart } from '../../lib/dates';

// Where tracker data lives.
//
// pgStore is the real one: PostgREST RPCs over Postgres with row-level security,
// so a member can only ever reach their own rows. That isolation is the reason
// this data is NOT in Directus — Community Edition cannot express it.
//
// localStore exists for development and demos only. Production never silently
// falls back to it: if the RPCs are missing, the UI says the tracker is being
// set up. Quietly writing worship logs to a device that will be wiped, while the
// member believes they are saved, is worse than an honest empty state.

const LOCAL_KEY = 'ihsanone_tracker_local';
const OUTBOX_KEY = 'ihsanone_tracker_outbox';

const readJson = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode */
  }
};

/**
 * Stable key for one log, so a double tap on a weak connection cannot produce
 * two rows. The server upserts on this.
 */
export function idempotencyKey(trackerId, date) {
  return `${trackerId}:${date}`;
}

// ---------------------------------------------------------------------------
// Offline outbox
// ---------------------------------------------------------------------------

export function queuedLogs() {
  return readJson(OUTBOX_KEY, []);
}

function enqueue(payload) {
  const queue = queuedLogs()
    .filter((item) => item.p_idempotency_key !== payload.p_idempotency_key);
  queue.push(payload);
  writeJson(OUTBOX_KEY, queue);
}

function dequeue(key) {
  writeJson(OUTBOX_KEY, queuedLogs().filter((item) => item.p_idempotency_key !== key));
}

export function clearOutbox() {
  writeJson(OUTBOX_KEY, []);
}

/** Push anything queued while offline. Safe to call repeatedly. */
export async function flushOutbox() {
  const queue = queuedLogs();
  if (!queue.length) return { flushed: 0 };
  let flushed = 0;
  for (const payload of queue) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await rpc('trk_log', payload);
      dequeue(payload.p_idempotency_key);
      flushed += 1;
    } catch {
      break; // still offline, or the server is unhappy — try again later
    }
  }
  return { flushed };
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { flushOutbox(); });
}

// ---------------------------------------------------------------------------
// pgStore — the real one
// ---------------------------------------------------------------------------

const pgStore = {
  name: 'pg',

  async list() {
    return (await rpc('trk_list', {})) || [];
  },

  async upsert(tracker) {
    return rpc('trk_upsert', { p_tracker: tracker });
  },

  async setBaseline(trackerId, baseline, effectiveFrom = sgToday()) {
    // Never rewrites history: this inserts a new effective-dated row so past
    // days stay judged against the line that applied when they were logged.
    return rpc('trk_set_baseline', {
      p_tracker_id: trackerId,
      p_baseline: baseline,
      p_effective_from: effectiveFrom,
    });
  },

  async log({ trackerId, date = sgToday(), value, detail, state = 'logged' }) {
    const payload = {
      p_tracker_id: trackerId,
      p_entry_date: date,
      p_value: value ?? null,
      p_detail: detail ?? null,
      p_state: state,
      // Named to match the RPC parameter (db/migrations/2609_002_tracker_core.sql).
      p_idempotency_key: idempotencyKey(trackerId, date),
    };
    try {
      return await rpc('trk_log', payload);
    } catch (err) {
      // A log must never be lost because the member walked into a lift.
      if (!err?.status) {
        enqueue(payload);
        return { queued: true };
      }
      throw err;
    }
  },

  async week(weekStart = sgWeekStart()) {
    return (await rpc('trk_week', { p_start: weekStart })) || [];
  },

  /** Everything one screen needs, in as few round trips as the API allows. */
  async snapshot(weekStart = sgWeekStart()) {
    const [trackers, week] = await Promise.all([
      rpc('trk_list', {}),
      rpc('trk_week', { p_start: weekStart }),
    ]);
    return {
      trackers: trackers || [],
      // trk_week returns the entries for the window plus the baselines in force
      // across it, so the client never has to guess which line applied when.
      entries: week?.entries || week || [],
      baselines: week?.baselines || [],
    };
  },

  async exportAll() {
    return rpc('trk_export', {});
  },

  async deleteAll() {
    return rpc('trk_delete_all', {});
  },
};

// ---------------------------------------------------------------------------
// localStore — development and demos only
// ---------------------------------------------------------------------------

const emptyLocal = () => ({ trackers: [], baselines: [], entries: [] });

const localStore = {
  name: 'local',

  async list() {
    return readJson(LOCAL_KEY, emptyLocal()).trackers;
  },

  async upsert(tracker) {
    const data = readJson(LOCAL_KEY, emptyLocal());
    const id = tracker.id || `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const next = { ...tracker, id };
    data.trackers = [...data.trackers.filter((t) => t.id !== id), next];
    writeJson(LOCAL_KEY, data);
    return next;
  },

  async setBaseline(trackerId, baseline, effectiveFrom = sgToday()) {
    const data = readJson(LOCAL_KEY, emptyLocal());
    data.baselines = [
      ...data.baselines.filter(
        (row) => !(row.tracker_id === trackerId && row.effective_from === effectiveFrom),
      ),
      { tracker_id: trackerId, baseline, effective_from: effectiveFrom },
    ];
    writeJson(LOCAL_KEY, data);
    return true;
  },

  async log({ trackerId, date = sgToday(), value, detail, state = 'logged' }) {
    const data = readJson(LOCAL_KEY, emptyLocal());
    const key = idempotencyKey(trackerId, date);
    data.entries = [
      ...data.entries.filter((entry) => idempotencyKey(entry.tracker_id, entry.entry_date) !== key),
      { tracker_id: trackerId, entry_date: date, value, detail, state },
    ];
    writeJson(LOCAL_KEY, data);
    return true;
  },

  async week() {
    return readJson(LOCAL_KEY, emptyLocal()).entries;
  },

  async snapshot() {
    const data = readJson(LOCAL_KEY, emptyLocal());
    return { trackers: data.trackers, entries: data.entries, baselines: data.baselines };
  },

  async remove(trackerId) {
    const data = readJson(LOCAL_KEY, emptyLocal());
    data.trackers = data.trackers.filter((t) => t.id !== trackerId);
    writeJson(LOCAL_KEY, data);
    return true;
  },

  async exportAll() {
    return readJson(LOCAL_KEY, emptyLocal());
  },

  async deleteAll() {
    writeJson(LOCAL_KEY, emptyLocal());
    return true;
  },
};

// ---------------------------------------------------------------------------

export function usingLocalStore() {
  return process.env.NODE_ENV !== 'production'
    && process.env.REACT_APP_TRACKER_STORE === 'local';
}

/** The store for this build. Production is always pgStore. */
export function getStore() {
  return usingLocalStore() ? localStore : pgStore;
}

/** Wipe device-side tracker traces. Called on sign-out. */
export function clearLocalTrackerData() {
  clearOutbox();
  try {
    window.localStorage.removeItem(LOCAL_KEY);
  } catch {
    /* ignore */
  }
}

export { pgStore, localStore };
